import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { AuthUser, resolveEffectiveRoutePermissions, resolveRoleLabel, rolePermissions, routePermissions, UserRole, StaffInvite } from "@/config/auth-config";
import { useNavigate } from "react-router-dom";
import { api, ApiError, storeAuthToken, storeRefreshToken, clearAuthToken, refreshAuthToken } from "@/utils/api-client";

type SignInResult = {
  ok: boolean;
  message: string;
  requiresOtp?: boolean;
  challengeId?: string;
  otpExpiresAt?: string;
  debugOtp?: string;
  redirectTo?: string;
};

type VerifyOtpResult = {
  ok: boolean;
  message: string;
  redirectTo?: string;
};

interface AuthContextValue {
  hydrated: boolean;
  user: AuthUser | null;
  appScope: "business" | "platform-admin";
  resolveRoleLabel: typeof resolveRoleLabel;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  verifySignInOtp: (challengeId: string, otp: string) => Promise<VerifyOtpResult>;
  signOut: () => void;
  updatePassword: (args: { currentPassword: string; newPassword: string }) => Promise<{ ok: boolean; message: string }>;
  updateProfile: (args: { name?: string; phone?: string; avatarUrl?: string }) => Promise<{ ok: boolean; message: string }>;
  updateUser: (updates: Partial<AuthUser>) => void;
  getInviteByToken: (token: string) => StaffInvite | null;
  acceptInvite: (token: string, password: string) => { ok: boolean; message: string };
  canAccessRoute: (pathname: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Pre-sign-out cleanup hooks. The native business app registers push-unregister
// here so it runs while the auth token is still valid. Kept out of the context so
// shared code carries no platform-specific (Capacitor) dependency.
type PreSignOutHook = () => void | Promise<void>;
const preSignOutHooks = new Set<PreSignOutHook>();

export function registerPreSignOutHook(hook: PreSignOutHook): () => void {
  preSignOutHooks.add(hook);
  return () => { preSignOutHooks.delete(hook); };
}

function normalizePath(pathname: string) {
  return pathname === "/" ? pathname : pathname.replace(/\/$/, "");
}

function getDefaultRoute(role: UserRole) {
  return role === "super_admin" ? "/console" : "/";
}

export function AuthProvider({ children, appScope = "business" }: { children: ReactNode; appScope?: "business" | "platform-admin" }) {
  const [hydrated, setHydrated] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const navigate = useNavigate();

  // Scoped localStorage key — keeps business and platform-admin sessions isolated
  const SESSION_KEY = appScope === "platform-admin" ? "rentos_adm_session" : "rentos_biz_session";

  // Hydrate from localStorage on mount
  useEffect(() => {
    const sessionStr = window.localStorage.getItem(SESSION_KEY);
    if (sessionStr) {
      try {
        const parsedUser = JSON.parse(sessionStr);
        if (parsedUser && rolePermissions[parsedUser.role as UserRole]) {
          setUser(parsedUser);
        } else {
          window.localStorage.removeItem(SESSION_KEY);
          setUser(null);
        }
      } catch {
        window.localStorage.removeItem(SESSION_KEY);
        setUser(null);
      }
    }
    setHydrated(true);
  }, [SESSION_KEY]);

  // If the plan fetch failed at login (sentinel value), retry automatically after 3s.
  // This covers the case where the backend was briefly unavailable during login.
  // Only runs in the business app.
  useEffect(() => {
    if (appScope !== "business") return;
    if (!user || user.role === "super_admin") return;
    if (!Array.isArray(user.allowedModules) || !user.allowedModules.includes("__fetch_failed__")) return;

    const timer = setTimeout(async () => {
      try {
        const [planData, profileData] = await Promise.all([
          api.silent.get<{ allowedModules: string[] }>("/api/business/plan"),
          api.silent.get<{ status: string }>("/api/business/profile"),
        ]);
        const freshModules = planData.allowedModules ?? [];
        const rawStatus = profileData.status ?? "active";
        const freshStatus: AuthUser["businessStatus"] =
          rawStatus === "suspended" || rawStatus === "blocked"
            ? (rawStatus as "suspended" | "blocked")
            : "active";
        const updatedUser: AuthUser = { ...user, allowedModules: freshModules, businessStatus: freshStatus };
        setUser(updatedUser);
        window.localStorage.setItem(SESSION_KEY, JSON.stringify(updatedUser));
      } catch {
        // Still failing — leave sentinel in place, will retry on next focus
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [appScope, user, SESSION_KEY]);

  // Proactive token refresh — silently renews the access token every 6 days
  // so the session never expires while the app is open, regardless of idle time.
  // The refresh token lasts 30 days; refreshing every 6 days keeps it well within that window.
  useEffect(() => {
    if (!user) return;

    const SIX_DAYS_MS = 6 * 24 * 60 * 60 * 1000;

    const doRefresh = async () => {
      // Use the shared api-client refresh so the correct API base is targeted.
      // On native (Capacitor) window.location.origin is https://localhost /
      // capacitor://localhost, which is NOT the API host — building the URL from
      // it would make every proactive refresh fail. refreshAuthToken() resolves
      // the base via VITE_API_BASE_URL and persists the new token on success.
      try {
        await refreshAuthToken();
      } catch {
        // Network error — ignore, will retry on next interval
      }
    };

    const interval = setInterval(doRefresh, SIX_DAYS_MS);
    return () => clearInterval(interval);
  }, [user]);

  // Re-fetch plan modules and business status on window focus for business users.
  // This ensures Platform Admin changes (plan upgrades, suspensions) propagate
  // to the Business App without requiring a full re-login.
  // Only runs in the business app — platform-admin has no plan/module concept.
  // Also runs once on mount to catch stale session data.
  useEffect(() => {
    if (appScope !== "business") return;
    
    let isMounted = true;

    const handleFocus = async () => {
      if (!isMounted) return; // Don't update if unmounted
      
      const sessionStr = window.localStorage.getItem(SESSION_KEY);
      if (!sessionStr) return;
      let currentUser: AuthUser | null = null;
      try { currentUser = JSON.parse(sessionStr); } catch { return; }
      if (!currentUser || currentUser.role === "super_admin") return;

      try {
        const [planData, profileData] = await Promise.all([
          api.silent.get<{ allowedModules: string[] }>("/api/business/plan"),
          api.silent.get<{ status: string }>("/api/business/profile"),
        ]);

        if (!isMounted) return; // Check again before updating state

        const freshModules = planData.allowedModules ?? [];
        const rawStatus = profileData.status ?? "active";
        const freshStatus: AuthUser["businessStatus"] =
          rawStatus === "suspended" || rawStatus === "blocked"
            ? (rawStatus as "suspended" | "blocked")
            : "active";

        const modulesChanged =
          JSON.stringify(freshModules.slice().sort()) !==
          JSON.stringify((currentUser.allowedModules ?? []).slice().sort());
        const statusChanged = freshStatus !== currentUser.businessStatus;

        if (modulesChanged || statusChanged) {
          const updatedUser: AuthUser = {
            ...currentUser,
            allowedModules: freshModules,
            businessStatus: freshStatus,
          };
          setUser(updatedUser);
          window.localStorage.setItem(SESSION_KEY, JSON.stringify(updatedUser));
        }
      } catch {
        // Network error or 401 — keep existing session, never log out on focus.
        // The user will be logged out naturally when they make an authenticated
        // action and the refresh token is also expired.
      }
    };

    window.addEventListener("focus", handleFocus);

    // Also run immediately on mount to catch stale session data
    handleFocus();
    
    return () => {
      isMounted = false;
      window.removeEventListener("focus", handleFocus);
    };
  }, [appScope, SESSION_KEY]);

  const value = useMemo<AuthContextValue>(() => ({
    hydrated,
    user,
    appScope,
    resolveRoleLabel,

    async signIn(email, password) {
      // ── OTP-based authentication ─────────────────────────────────────────
      // Two-step login: email+password → OTP sent to email → verify OTP.
      try {
        const data = await api.post<{
          challengeId: string;
          email: string;
          expiresAt: string;
          debugOtp?: string;
        }>("/api/auth/request-otp", { email, password, appScope });

        return {
          ok: true,
          message: "OTP sent to your email.",
          requiresOtp: true,
          challengeId: data.challengeId,
          otpExpiresAt: data.expiresAt,
          debugOtp: process.env.NODE_ENV !== "production" ? (data.debugOtp || undefined) : undefined,
        };
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Network error. Is the backend running?";
        return { ok: false, message };
      }
    },

    async verifySignInOtp(challengeId, otp) {
      try {
        const data = await api.post<{
          user: AuthUser & { isOwner?: boolean };
          token: string;
          refreshToken?: string;
        }>("/api/auth/verify-otp", { challengeId, otp });

        const loggedInUser = data.user;

        let allowedModules: string[] = [];
        let businessStatus: "active" | "suspended" | "blocked" = "active";

        if (loggedInUser.role !== "super_admin") {
          try {
            const [planData, profileData] = await Promise.all([
              api.silent.get<{ allowedModules: string[] }>("/api/business/plan"),
              api.silent.get<{ status: string }>("/api/business/profile"),
            ]);
            allowedModules = planData.allowedModules ?? [];
            const fetchedStatus = profileData.status;
            if (fetchedStatus === "suspended" || fetchedStatus === "blocked") {
              businessStatus = fetchedStatus as "suspended" | "blocked";
            }
          } catch {
            allowedModules = ["__fetch_failed__"];
          }
        }

        const userWithModules: AuthUser = {
          ...loggedInUser,
          allowedModules,
          businessStatus,
          isOwner: loggedInUser.isOwner ?? false,
        };

        setUser(userWithModules);
        window.localStorage.setItem(SESSION_KEY, JSON.stringify(userWithModules));
        // Store the JWT as Bearer token to avoid production cookie collision
        // where both apps share the same auth_token cookie on .rentos.tech.
        if (data.token) {
          storeAuthToken(data.token);
        }
        // Persist the refresh token on native so refresh works without the
        // cross-site cookie (no-op on web).
        if (data.refreshToken) {
          storeRefreshToken(data.refreshToken);
        }

        if (userWithModules.mustChangePassword) {
          const changeRoute = "/set-password?force=1";
          return {
            ok: true,
            message: "Sign-in complete. You must change your password before continuing.",
            redirectTo: changeRoute,
          };
        }

        return {
          ok: true,
          message: "Signed in successfully.",
          redirectTo: getDefaultRoute(userWithModules.role),
        };
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Network error. Is the backend running?";
        return { ok: false, message };
      }
    },

    async signOut() {
      // Run any registered pre-sign-out cleanup (e.g. native push unregister)
      // while the auth token is still valid, before we clear it below.
      for (const hook of preSignOutHooks) {
        try { await hook(); } catch { /* best-effort */ }
      }
      try {
        await api.post("/api/auth/logout");
      } catch (err) {
        console.error("Logout error", err);
      }
      setUser(null);
      window.localStorage.removeItem(SESSION_KEY);
      clearAuthToken();
      navigate("/signin");
    },

    canAccessRoute(pathname) {
      if (!user) return false;
      const normalized = normalizePath(pathname);
      const permission = routePermissions[normalized];
      if (!permission) return true;
      const permissionsForRole = resolveEffectiveRoutePermissions(user);
      if (!permissionsForRole) return false;
      return permissionsForRole.includes(permission);
    },

    async updatePassword({ currentPassword, newPassword }) {
      try {
        const result = await api.post<{ ok: boolean; token?: string }>("/api/auth/update-password", { currentPassword, newPassword });
        if (user) {
          const updatedUser = { ...user, mustChangePassword: false };
          setUser(updatedUser);
          window.localStorage.setItem(SESSION_KEY, JSON.stringify(updatedUser));
        }
        // Backend issues a new token after password change — store it
        if (result && (result as any).token) {
          storeAuthToken((result as any).token);
        }
        return { ok: true, message: "Password updated successfully." };
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Network error. Is the backend running?";
        return { ok: false, message };
      }
    },

    async updateProfile({ name, phone, avatarUrl }) {
      try {
        const body: Record<string, unknown> = {};
        if (name !== undefined) body.name = name;
        if (phone !== undefined) body.phone = phone;
        if (avatarUrl !== undefined) body.avatarUrl = avatarUrl;
        const updatedProfile = await api.put<{ id: string; name: string; email: string; phone?: string; avatarUrl?: string }>(
          "/api/auth/profile",
          body,
        );
        if (user) {
          const updatedUser = {
            ...user,
            name: updatedProfile.name,
            phone: updatedProfile.phone,
            avatarUrl: updatedProfile.avatarUrl,
          };
          setUser(updatedUser);
          window.localStorage.setItem(SESSION_KEY, JSON.stringify(updatedUser));
        }
        return { ok: true, message: "Profile updated successfully." };
      } catch (err) {
        const message = err instanceof ApiError ? err.message : "Network error. Is the backend running?";
        return { ok: false, message };
      }
    },

    updateUser(updates: Partial<AuthUser>) {
      if (user) {
        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        window.localStorage.setItem(SESSION_KEY, JSON.stringify(updatedUser));
      }
    },

    getInviteByToken(_token) {
      return null;
    },

    acceptInvite(_token, _password) {
      return { ok: false, message: "Use the secure invite page to finish account setup." };
    },
  }), [hydrated, user, navigate, appScope, SESSION_KEY]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
